import React from "react";
import PlusIcon from "@heroicons/react/24/outline/PlusIcon";
import {
  Box,
  Stack,
  SvgIcon,
} from "@mui/material";
import { PageHeader } from "../../components/page-header";
import { useSelection } from "../../hooks/use-selection";
import { CustomTable } from "../../components/custom-table";
import { CustomSearch } from "../../components/custom-search";
import { smsContactPhoneMappingsHeadCells } from "../../seed/table-headers";
import TrashIcon from "@heroicons/react/24/outline/TrashIcon";
import { ConfirmationDialog } from "../../components/confirmation-dialog";
import { CREATE, ROWSPERPAGE, UPDATE } from "../../utils/constant";
import { authPostRequest } from "../../services/api-service";
import {
  createUserSMSContactPhoneMappingUrl,
  deleteUserSMSContactPhoneMappingUrl,
  getAllUserSMSContactPhoneMappingsByPaginationUrl,
  updateUserSMSContactPhoneMappingUrl,
} from "../../seed/url";
import { CustomAlert } from "../../components/custom-alert";
import { FormDialog } from "../../components/form-dialog";
import { smsContactPhoneMappingFormFields } from "../../seed/form-fields";
import PencilIcon from "@heroicons/react/24/outline/PencilIcon";

const useMappingIds = (mappings) => {
  return React.useMemo(() => {
    return mappings.map((item) => item.id);
  }, [mappings]);
};

function SMSContactPhoneMappings() {
  const [action, setAction] = React.useState(CREATE);
  const [rowsPerPage, setRowsPerPage] = React.useState(ROWSPERPAGE);
  const [mappings, setMappings] = React.useState({
    page: 1,
    total_results: 0,
    total_pages: 0,
    results: [],
  });
  const [selectedData, setSelectedData] = React.useState({});
  const [searchTerm, setSearchTerm] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(true);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const mappingIds = useMappingIds(mappings.results);
  const mappingSelection = useSelection(mappingIds);
  const [openCreateDialog, setOpenCreateDialog] = React.useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = React.useState(false);
  const [openAlert, setOpenAlert] = React.useState(false);
  const [severity, setSeverity] = React.useState("");
  const [severityMessage, setSeverityMessage] = React.useState("");
  const values = [
    {
      id: action === UPDATE ? selectedData.id : 0,
      user_id: action === UPDATE ? selectedData.user_id : 0,
      phone_number: action === UPDATE ? selectedData.phone_number : "",
    },
  ];
  const [order, setOrder] = React.useState("desc");
  const [orderBy, setOrderBy] = React.useState("id");

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const fetcher = React.useCallback(
    (page) => {
      setIsLoading(true);
      authPostRequest(
        getAllUserSMSContactPhoneMappingsByPaginationUrl,
        {
          query: searchTerm,
          sort: orderBy + " " + order,
          limit: rowsPerPage,
          page: page,
        },
        (data) => {
          setMappings(data);
          setIsLoading(false);
        },
        () => {
          setMappings({
            page: 1,
            total_results: 0,
            total_pages: 0,
            results: [],
          });
          setIsLoading(false);
        }
      );
    },
    [rowsPerPage, searchTerm, orderBy, order]
  );

  React.useEffect(() => {
    fetcher(1);
  }, [fetcher]);

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handlePageChange = React.useCallback(
    (event, value) => {
      fetcher(value + 1);
    },
    [fetcher]
  );

  const handleRowsPerPageChange = React.useCallback((event) => {
    setRowsPerPage(event.target.value);
  }, []);

  const handleClickOpenCreateDialog = () => {
    setOpenCreateDialog(true);
  };

  const handleCloseCreateDialog = () => {
    action === UPDATE ? fetcher(mappings.page) : fetcher(1);
    setOpenCreateDialog(false);
    setAction(CREATE);
  };

  const handleClickOpenDeleteDialog = () => {
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
  };

  const handleClickAlert = () => {
    setOpenAlert(true);
  };

  const handleCloseAlert = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenAlert(false);
  };

  const onSelect = (data) => {
    setSelectedData(data);
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    authPostRequest(
      deleteUserSMSContactPhoneMappingUrl,
      {
        id: selectedData.id,
      },
      (data) => {
        fetcher(mappings.page);
        setSeverityMessage(data.message);
        setSeverity("success");
        handleClickAlert();
        setIsDeleting(false);
        handleCloseDeleteDialog();
      },
      (error) => {
        if (error?.response?.data?.message) {
          setSeverityMessage(error.response.data.message[0]);
          setSeverity("error");
          handleClickAlert();
        }
        setIsDeleting(false);
      }
    );
  };

  const contentPopoverItems = [
    {
      id: "edit",
      label: "Edit",
      icon: (
        <SvgIcon fontSize="small" sx={{ color: "text.primary" }}>
          <PencilIcon />
        </SvgIcon>
      ),
      onClick: () => {
        setAction(UPDATE);
        handleClickOpenCreateDialog();
      },
    },
    {
      id: "delete",
      label: "Delete",
      icon: (
        <SvgIcon fontSize="small" sx={{ color: "text.primary" }}>
          <TrashIcon />
        </SvgIcon>
      ),
      onClick: () => {
        handleClickOpenDeleteDialog();
      },
    },
  ];

  return (
    <>
      {openAlert && (
        <CustomAlert
          openAlert={openAlert}
          handleCloseAlert={handleCloseAlert}
          severity={severity}
          severityMessage={severityMessage}
        />
      )}
      {openCreateDialog && (
        <FormDialog
          open={openCreateDialog}
          handleClose={handleCloseCreateDialog}
          dialogTitle={"SMS Contact Phone Mapping"}
          action={action}
          fields={smsContactPhoneMappingFormFields}
          values={values}
          url={
            action === CREATE
              ? createUserSMSContactPhoneMappingUrl
              : updateUserSMSContactPhoneMappingUrl
          }
        />
      )}
      {openDeleteDialog && (
        <ConfirmationDialog
          open={openDeleteDialog}
          handleClose={handleCloseDeleteDialog}
          handleAction={handleDelete}
          isPerformingAction={isDeleting}
          dialogTitle={"Delete Alert"}
          dialogContentText={"Are you sure you want to delete this SMS contact phone mapping?"}
        />
      )}
      <Box sx={{ width: "100%", maxWidth: { sm: "100%", md: "1700px" } }}>
        <Stack spacing={2}>
          <PageHeader
            title="SMS Contact Phones"
            subtitle="Map staff users to SMS contact numbers used in order status messages. Default fallback is 0743 377 129."
            actions={[
              {
                label: "Add",
                icon: <SvgIcon fontSize="small"><PlusIcon /></SvgIcon>,
                onClick: () => {
                  setAction(CREATE);
                  handleClickOpenCreateDialog();
                },
              },
            ]}
          />
          <CustomSearch handleSearch={handleSearch} />
          <CustomTable
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            count={mappings.total_results}
            items={mappings.results}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            onSelectOne={mappingSelection.handleSelectOne}
            onSelect={onSelect}
            page={mappings.page >= 1 ? mappings.page - 1 : mappings.page}
            rowsPerPage={rowsPerPage}
            selected={mappingSelection.selected}
            headCells={smsContactPhoneMappingsHeadCells}
            popoverItems={contentPopoverItems}
            isLoading={isLoading}
          />
        </Stack>
      </Box>
    </>
  );
}

export default SMSContactPhoneMappings;
